import { Divider, Flex, Input, Text } from "@mantine/core";
import { Fragment, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import DatePickerInput from "src/components/shared/_FormInputs/DatePickerInput";
import Switch from "src/components/shared/_FormInputs/Switch";
import { useCheckPerm, useIsOnline } from "src/hooks";
import { biofoulingSetupFields } from "src/pages/Settings/YachtSetup/BiofoulingSetUp/BiofoulingSetUp.utils";
import {
  getNextFieldLabel,
  getNextFieldName,
  gmdssSetupFields,
} from "src/pages/Settings/YachtSetup/GmdssSetup/GmdssSetup.utils";
import { type TYachtSetupField } from "src/pages/Settings/YachtSetup/yachtSetup.types";
import { InputStyledWrapper } from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup.styled";
import { vesselParticularsFields } from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup.utils";

import { Button } from "../../../shared/Button/Button";
import VesselTypeInput from "./VesselParticulars/VesselTypeInput";
import { IomData } from "../../../../pages/Settings/YachtSetup/VesselParticulars/YachtSetup.types";
import IomInputs from "./VesselParticulars/IomInputs/IomInputs";

const dataFields = {
  vessel: vesselParticularsFields,
  gmdss: gmdssSetupFields,
  biofoul: biofoulingSetupFields,
};

interface Props {
  type: keyof typeof dataFields;
  dynamicFields?: Record<string, any>;
  iomData?: Record<string, IomData>;
}

const YachtDetailsForm = ({ type, dynamicFields, iomData }: Props) => {
  const { register, control, reset, getValues } = useFormContext();
  const isOnline = useIsOnline();
  const checkPerm = useCheckPerm();
  const readOnly = !checkPerm("editparticulars") || !isOnline;
  const [fields, setFields] = useState(dataFields);

  useEffect(() => {
    if (dynamicFields) {
      const copiedFields = JSON.parse(JSON.stringify(fields));
      Object.keys(dynamicFields).forEach(key => {
        const isArrayPath = /^[a-z]+\.\d+\..+$/.test(key);
        const timesToAdd = isArrayPath
          ? dynamicFields[key].length - 1
          : dynamicFields[key].length / 2 - 1;
        Array.from({ length: timesToAdd }, () => {
          handleAddField(key, copiedFields);
        });
      });
      setFields(copiedFields);
    }
  }, []);

  const handleAddField = (section: string, copiedFields?: any) => {
    const data = copiedFields ?? fields;
    const arrayMatch = section.match(/^([a-z]+)\.\d+\.(.+)$/);
    const lastField = arrayMatch
      ? data[type].findLast(
          (el: TYachtSetupField) =>
            new RegExp(`^${arrayMatch[1]}\\.\\d+\\.${arrayMatch[2]}$`).test(
              el.name ?? "",
            ),
        )
      : data[type].findLast((el: TYachtSetupField) => el.name?.includes(section));
    const indexOfLastField = data[type].indexOf(lastField);
    const preLastField = data[type][indexOfLastField - 1];

    const newFields = [
      {
        name: getNextFieldName(preLastField.name ?? ""),
        label: getNextFieldLabel(preLastField),
      },
      {
        name: getNextFieldName(lastField.name ?? ""),
        label: getNextFieldLabel(lastField),
      },
    ];
    if (!copiedFields) {
      setFields(prevFields => ({
        ...prevFields,
        [type]: [
          ...prevFields[type].slice(0, indexOfLastField + 1),
          ...newFields,
          ...prevFields[type].slice(indexOfLastField + 1),
        ],
      }));
      if (arrayMatch) {
        const arrName = arrayMatch[1];
        const currentValues = getValues();
        const newArr = [
          ...((currentValues as any)[arrName] ?? []),
          { serial: "", exp: "" },
        ];
        reset({ ...currentValues, [arrName]: newArr });
      } else {
        reset({ ...getValues(), [newFields[0].name]: "", [newFields[1].name]: "" });
      }
    } else {
      copiedFields[type] = [
        ...copiedFields[type].slice(0, indexOfLastField + 1),
        ...newFields,
        ...copiedFields[type].slice(indexOfLastField + 1),
      ];
      if (!arrayMatch) {
        reset({ ...getValues(), [newFields[0].name]: "", [newFields[1].name]: "" });
      }
    }
  };
  return (
    <Flex
      maw="600px"
      w="100%"
      direction="column"
      align="center"
      mx="auto"
      mt={30}
      gap={10}>
      {fields[type].map((field, i) => {
        if (field?.line) {
          return (
            <Divider
              mt="sm"
              label={field.label}
              labelPosition="center"
              styles={{ root: { width: "90%" } }}
            />
          );
        } else if (dynamicFields && "button" in field && field?.button) {
          return (
            <Button onClick={() => handleAddField(field.type ?? "")}>
              {field.label}
            </Button>
          );
        } else {
          return (
            <InputStyledWrapper>
              <Text size={"sm"} w="50%">
                {field.label}
              </Text>
              {field.type === "date" ? (
                <DatePickerInput
                  w="50%"
                  control={control}
                  name={field.name ?? ""}
                />
              ) : (
                <Input
                  w={"50%"}
                  {...register(field.name ?? "")}
                  readOnly={readOnly}
                />
              )}
            </InputStyledWrapper>
          );
        }
      })}

      {type === "vessel" && (
        <Fragment>
          <InputStyledWrapper>
            <Text size={"sm"} w="50%">
              Logbook Working Language
            </Text>
            <Input w={"50%"} {...register("lblang")} readOnly={readOnly} />
          </InputStyledWrapper>
          <VesselTypeInput />
          <InputStyledWrapper>
            <Text size={"sm"} w="50%">
              Transform all entries to uppercase
            </Text>
            <Switch name="uppercase" control={control} disabled={readOnly} />
          </InputStyledWrapper>
          {iomData && (
            <Fragment>
              <Divider label="IOM" w="70%" labelPosition="center" />
              <IomInputs iomData={iomData} />
            </Fragment>
          )}
        </Fragment>
      )}
    </Flex>
  );
};

export default YachtDetailsForm;
